import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { CalendarDays, Clock } from "lucide-react";
import { format, differenceInDays } from "date-fns";
import { getAvatarUrl } from "@/utils/storageUtils";
import { useNavigate } from "react-router-dom";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";

type VacationWithProfile = {
  id: string;
  user_id: string;
  start_date: string;
  end_date: string;
  status: string;
  is_half_day: boolean;
  reason: string;
  profiles: {
    full_name: string;
    avatar_url: string;
    role: string;
  };
};

export const UpcomingVacationsCarousel = () => {
  const navigate = useNavigate();
  const { data: vacations } = useQuery({
    queryKey: ["upcoming_vacations_carousel"],
    queryFn: async () => {
      const today = new Date().toISOString().split("T")[0];
      const { data, error } = await supabase
        .from("vacation_requests")
        .select(`
          *,
          profiles (
            full_name,
            avatar_url,
            role
          )
        `)
        .eq("status", "approved")
        .gte("end_date", today)
        .order("start_date");

      if (error) throw error;
      return data as VacationWithProfile[];
    },
  });

  if (!vacations || vacations.length === 0) return null;

  return (
    <div className="w-full relative">
      <Carousel
        opts={{ align: "start", loop: false }}
        className="w-full"
      >
        <CarouselContent className="-ml-4">
          {vacations.map((vacation) => {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const startDate = new Date(vacation.start_date);
            startDate.setHours(0, 0, 0, 0);
            const endDate = new Date(vacation.end_date);
            endDate.setHours(0, 0, 0, 0);
            const isActive = today >= startDate && today <= endDate;
            const daysFromNow = differenceInDays(startDate, today);
            const diffDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;

            const badgeLabel = isActive ? "NOW" : daysFromNow === 0 ? "TODAY" : `${daysFromNow}`;
            const badgeSub = isActive ? "Active" : daysFromNow === 0 ? "Starts" : "days";

            const accentColor = isActive
              ? "bg-blue-600"
              : daysFromNow === 0
              ? "bg-amber-600"
              : daysFromNow <= 7
              ? "bg-emerald-600"
              : "bg-sky-600";

            return (
              <CarouselItem key={vacation.id} className="pl-4 basis-1/3">
                <div
                  className={`flex rounded-xl border overflow-hidden h-full transition-all hover:shadow-lg ${
                    isActive
                      ? "bg-blue-50/50 border-blue-200"
                      : "bg-card border-border hover:border-muted-foreground/30"
                  }`}
                >
                  {/* Left column - big number */}
                  <div className="flex flex-col items-center justify-center flex-shrink-0 px-5 py-8 min-w-[80px]">
                    <span className={`text-4xl font-black leading-none ${
                      isActive ? "text-blue-600" : daysFromNow === 0 ? "text-orange-500" : daysFromNow <= 7 ? "text-green-600" : "text-slate-600"
                    }`}>
                      {badgeLabel}
                    </span>
                    <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mt-0.5">
                      {badgeSub}
                    </span>
                  </div>

                  {/* Divider */}
                  <div className="w-px bg-border self-stretch my-3" />

                  {/* Right column - info + date bar */}
                  <div className="flex flex-col flex-1 min-w-0 px-4 py-6 gap-4">
                    {/* Avatar + info */}
                    <div
                      className="flex items-center gap-3 flex-1 cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={() => navigate(`/profile/${vacation.user_id}`)}
                    >
                      <Avatar className="h-10 w-10 flex-shrink-0">
                        <AvatarImage src={getAvatarUrl(vacation.profiles.avatar_url)} className="object-cover" />
                        <AvatarFallback className="text-xs font-medium">
                          {vacation.profiles.full_name?.split(" ").map((n) => n[0]).join("")}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <p className="font-semibold text-sm text-foreground truncate">
                          {vacation.profiles.full_name}
                        </p>
                        <p className="text-xs text-muted-foreground capitalize">
                          {vacation.profiles.role}
                        </p>
                      </div>
                    </div>

                    {/* Bottom bar with date info */}
                    <div className={`${accentColor} px-3 py-1.5 flex items-center justify-between text-white text-xs rounded-md`}>
                      <div className="flex items-center gap-1.5">
                        <CalendarDays className="h-3.5 w-3.5" />
                        <span className="font-medium">
                          {format(startDate, "MMM d")} → {format(endDate, "MMM d")}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        <span>{diffDays}d{vacation.is_half_day ? " (½)" : ""}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CarouselItem>
            );
          })}
        </CarouselContent>
        <CarouselPrevious className="-left-10" />
        <CarouselNext className="-right-10" />
      </Carousel>
    </div>
  );
};
